import type { IconType } from 'react-icons';
import {
  LuArrowRight,
  LuCircleCheck,
  LuCompass,
  LuHandHeart,
  LuShieldCheck,
  LuVideo,
} from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../design-system/components';
import { useOnboardingStore } from '../../stores/onboarding.store';
import CelebrationPanel from './CelebrationPanel';

interface EmpathyUnlockPanelProps {
  onClose: () => void;
}

function Feature({ icon: Icon, title, detail }: { icon: IconType; title: string; detail: string }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-[var(--ee-radius-md)] bg-[var(--ee-surface-warm)]">
      <span
        aria-hidden="true"
        className="w-9 h-9 rounded-full bg-[var(--ee-peach)] text-[var(--ee-orange-text)] flex items-center justify-center flex-shrink-0"
      >
        <Icon className="w-[18px] h-[18px]" />
      </span>
      <div>
        <p className="text-sm font-semibold text-[var(--ee-navy)]">{title}</p>
        <p className="text-xs text-[var(--ee-text-secondary)]">{detail}</p>
      </div>
    </div>
  );
}

export default function EmpathyUnlockPanel({ onClose }: EmpathyUnlockPanelProps) {
  const navigate = useNavigate();

  const handleExploreEmpathy = () => {
    onClose();
    // Navigate to Course page first so the E button is visible in the nav bar
    void navigate('/app');
    // Start the empathy page tour (spotlight E button → navigate → tour steps)
    useOnboardingStore.getState().startEmpathyTour();
  };

  return (
    <CelebrationPanel
      onClose={onClose}
      // Completion badge. Not "unlocked": since P5 the intro gates nothing
      // (COHORT-COMMUNICATION.md X1). What finishing it does change is the
      // intro-only matching filter (X3).
      eyebrow={
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--ee-success-soft)] text-[var(--ee-success-text)] text-xs font-semibold uppercase tracking-wider">
          <LuCircleCheck aria-hidden="true" className="w-3.5 h-3.5" />
          Introduction complete
        </span>
      }
      title="On-Demand Empathy"
      actions={
        <>
          <Button
            variant="primary"
            fullWidth
            leadingIcon={<LuCompass />}
            onClick={handleExploreEmpathy}
          >
            Explore On-Demand Empathy
          </Button>
          <Button variant="secondary" fullWidth leadingIcon={<LuArrowRight />} onClick={onClose}>
            Continue Learning
          </Button>
        </>
      }
    >
      <p className="text-[var(--ee-text-secondary)] text-sm leading-relaxed text-center mb-5 max-w-xs mx-auto">
        You&apos;ve completed the intro! Some members prefer to match only with people who finished
        it, so you&apos;re now eligible for those matches too.
      </p>

      <div className="space-y-2.5">
        <Feature icon={LuVideo} title="Live Empathy Sessions" detail="1-on-1 with real people" />
        <Feature
          icon={LuHandHeart}
          title="Give or Receive"
          detail="Practice both sides of empathy"
        />
        <Feature
          icon={LuShieldCheck}
          title="Safe Space"
          detail="Supportive, judgment-free environment"
        />
      </div>
    </CelebrationPanel>
  );
}
